// Emlak CMS — SSS (FAQ) API client (S13, real backend).
//
// All endpoints live under `/api/v1/sss/...` and return `ActionResponse<T>`.
// Single-record endpoints → `unwrap`; list endpoints → `unwrapList`.
// The `helpful` endpoint is **anonymous** on the backend (race-safe atomic
// counter); we still forward the auth header from the admin UI for audit.

import axios from 'axios';
import { http, unwrap, unwrapCreatedId, unwrapList, type ActionResponse } from './http';
import type {
  CreateSssInput,
  ReorderEntry,
  SssAnalyticsData,
  SssAnalyticsRow,
  SssFaq,
  SssListQuery,
  SssListResponse,
  SssVersionSnapshot,
  UpdateSssInput,
} from '@features/sss/types';
import { decodeSssStatus, sssStatusToBackend } from '@features/sss/types';

const baseURL = import.meta.env.VITE_API_BASE_URL ?? 'http://localhost:5001';

// ── Backend DTOs (raw wire format) ────────────────────────────────────────

interface BackendSssItem {
  id: string;
  question: string;
  answer?: string | null;
  categoryId: string | null;
  order: number;
  helpfulYesCount: number;
  helpfulNoCount: number;
  status: unknown;
  publishedAt: string | null;
  createdAt?: string | null;
  updatedAt: string;
  languageId?: string | null;
  groupId?: string | null;
  variantLanguageCodes?: string[] | null;
}

interface BackendSssVersion {
  id: string;
  sssItemId: string;
  versionNumber: number;
  question: string;
  answer: string;
  categoryId: string | null;
  order: number;
  authorId: string | null;
  authorEmail: string | null;
  changeNote: string | null;
  createdAt: string;
}

interface BackendAnalyticsRow {
  id: string;
  question: string;
  helpfulYesCount: number;
  helpfulNoCount: number;
  totalVotes: number;
  // BE field is `helpfulRatio` (SssAnalyticsRowDto); `yesRatio` kept as a
  // defensive fallback only.
  helpfulRatio: number;
  yesRatio?: number;
}

interface BackendAnalytics {
  totalItems: number;
  totalPublished?: number;
  publishedItems?: number;
  totalHelpfulYes?: number;
  totalYes?: number;
  totalHelpfulNo?: number;
  totalNo?: number;
  overallHelpfulRatio?: number;
  overallRatio?: number;
  mostHelpful: BackendAnalyticsRow[] | null;
  leastHelpful: BackendAnalyticsRow[] | null;
  mostVoted: BackendAnalyticsRow[] | null;
}

// ── Mappers ───────────────────────────────────────────────────────────────

function mapItem(b: BackendSssItem): SssFaq {
  return {
    id: b.id,
    question: b.question,
    answer: b.answer ?? '',
    categoryId: b.categoryId ?? null,
    order: b.order ?? 0,
    helpfulYesCount: b.helpfulYesCount ?? 0,
    helpfulNoCount: b.helpfulNoCount ?? 0,
    status: decodeSssStatus(b.status),
    publishedAt: b.publishedAt ?? null,
    createdAt: b.createdAt ?? b.updatedAt,
    updatedAt: b.updatedAt,
    languageId: b.languageId ?? '',
    groupId: b.groupId ?? '',
    variantLanguageCodes: b.variantLanguageCodes ?? [],
  };
}

function mapVersion(b: BackendSssVersion): SssVersionSnapshot {
  return {
    id: b.id,
    faqId: b.sssItemId,
    versionNumber: b.versionNumber,
    createdAt: b.createdAt,
    authorId: b.authorId ?? null,
    authorName: b.authorEmail ?? 'Sistem',
    note: b.changeNote ?? undefined,
    snapshot: {
      question: b.question,
      answer: b.answer,
      categoryId: b.categoryId ?? null,
      order: b.order ?? 0,
    },
  };
}

function mapAnalyticsRow(b: BackendAnalyticsRow): SssAnalyticsRow {
  return {
    id: b.id,
    question: b.question,
    helpfulYesCount: b.helpfulYesCount,
    helpfulNoCount: b.helpfulNoCount,
    totalVotes: b.totalVotes,
    yesRatio: b.helpfulRatio ?? b.yesRatio ?? 0,
  };
}

// ── Public client ─────────────────────────────────────────────────────────

export const sssApi = {
  async list(query: SssListQuery = {}): Promise<SssListResponse> {
    const params = new URLSearchParams();
    if (query.page) params.set('page', String(query.page));
    if (query.pageSize) params.set('pageSize', String(query.pageSize));
    const search = query.search ?? query.q;
    if (search) params.set('search', search);
    if (query.categoryId) params.set('categoryId', query.categoryId);
    // K-13 #19 — BE artık tam `?status=` filtresi destekliyor (6 durum);
    // PUBLISHED için eski publishedOnly çevirisine gerek kalmadı.
    if (query.status && query.status !== 'ALL') {
      params.set('status', sssStatusToBackend(query.status));
    }
    if (query.publishedOnly !== undefined) {
      params.set('publishedOnly', String(query.publishedOnly));
    }
    if (query.sortBy) params.set('sortBy', query.sortBy);
    if (query.languageId) params.set('languageId', query.languageId);

    const res = await http.get<ActionResponse<BackendSssItem>>(
      `/api/v1/sss?${params.toString()}`,
    );
    const { items, pagination } = unwrapList(res);
    return {
      items: items.map(mapItem),
      total: pagination?.totalCount ?? items.length,
      page: pagination?.page ?? query.page ?? 1,
      pageSize: pagination?.pageSize ?? query.pageSize ?? items.length,
      totalPages: pagination?.totalPages ?? 1,
    };
  },

  async get(id: string): Promise<SssFaq> {
    const res = await http.get<ActionResponse<BackendSssItem>>(`/api/v1/sss/${id}`);
    return mapItem(unwrap(res));
  },

  async create(input: CreateSssInput): Promise<SssFaq> {
    const res = await http.post<ActionResponse<string | { id: string }>>(`/api/v1/sss`, {
      question: input.question,
      answer: input.answer,
      categoryId: input.categoryId,
      order: input.order ?? null,
      // İçerik i18n — yalnız değer varsa gönder (varyant/dil seçimi).
      ...(input.languageId ? { languageId: input.languageId } : {}),
      ...(input.groupId ? { groupId: input.groupId } : {}),
    });
    const newId = unwrapCreatedId(res);
    // Fetch back to return a full FE object (so caller can navigate to /sss/:id).
    return sssApi.get(newId);
  },

  async update(id: string, input: UpdateSssInput): Promise<SssFaq> {
    const res = await http.put<ActionResponse<boolean>>(`/api/v1/sss/${id}`, {
      question: input.question,
      answer: input.answer,
      categoryId: input.categoryId,
      order: input.order ?? null,
      changeNote: input.changeNote ?? null,
    });
    unwrap(res);
    return sssApi.get(id);
  },

  async remove(id: string): Promise<void> {
    const res = await http.delete<ActionResponse<boolean>>(`/api/v1/sss/${id}`);
    unwrap(res);
  },

  /** Draft|Rejected → InReview. */
  async submitReview(id: string): Promise<SssFaq> {
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/sss/${id}/submit-review`,
    );
    unwrap(res);
    return sssApi.get(id);
  },

  /** Approve = InReview → Approved; pass `approve=false` (+ note) to reject → Rejected. */
  async approve(id: string, approve = true, note?: string): Promise<SssFaq> {
    const p = new URLSearchParams();
    p.set('approve', approve ? 'true' : 'false');
    if (note) p.set('note', note);
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/sss/${id}/approve?${p.toString()}`,
    );
    unwrap(res);
    return sssApi.get(id);
  },

  /** Approved|Archived|Draft → Published. Invalid source states throw INVALID_STATE (422). */
  async publish(id: string, publish = true): Promise<SssFaq> {
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/sss/${id}/publish?publish=${publish ? 'true' : 'false'}`,
    );
    unwrap(res);
    return sssApi.get(id);
  },

  /** Published|Approved → Archived. */
  async archive(id: string): Promise<SssFaq> {
    const res = await http.post<ActionResponse<boolean>>(`/api/v1/sss/${id}/archive`);
    unwrap(res);
    return sssApi.get(id);
  },

  async listVersions(id: string): Promise<{ items: SssVersionSnapshot[] }> {
    const res = await http.get<ActionResponse<BackendSssVersion>>(
      `/api/v1/sss/${id}/versions`,
    );
    const { items } = unwrapList(res);
    return { items: items.map(mapVersion) };
  },

  /**
   * Restore an SSS item to an earlier version. Backend identifies the target
   * by **version number** (not version row id). Callers in the FE may pass
   * either — we normalize via `findVersionNumber` when needed.
   */
  async restoreVersion(id: string, versionNumber: number): Promise<SssFaq> {
    const res = await http.post<ActionResponse<boolean>>(
      `/api/v1/sss/${id}/versions/${versionNumber}/restore`,
    );
    unwrap(res);
    return sssApi.get(id);
  },

  async reorder(entries: ReorderEntry[]): Promise<void> {
    // BE accepts `{ items: [...] }` envelope (consistent with other modules).
    const res = await http.post<ActionResponse<boolean>>(`/api/v1/sss/reorder`, {
      items: entries.map((e) => ({ id: e.id, order: e.order })),
    });
    unwrap(res);
  },

  /**
   * Record a helpful vote. Public on the backend (no auth header required) —
   * we use a bare axios call so anon mobile/web clients can share the same
   * client surface. From admin UI this is still wrapped in the normal token.
   */
  async voteHelpful(id: string, helpful: boolean): Promise<void> {
    const res = await axios.post<ActionResponse<boolean>>(
      `${baseURL}/api/v1/sss/${id}/helpful?helpful=${helpful ? 'true' : 'false'}`,
      undefined,
      { headers: { Accept: 'application/json' } },
    );
    if (!res.data?.isSuccessful) {
      throw new Error(res.data?.message ?? 'Vote failed.');
    }
  },

  async analytics(top = 10): Promise<SssAnalyticsData> {
    const res = await http.get<ActionResponse<BackendAnalytics>>(
      `/api/v1/sss/analytics?top=${top}`,
    );
    const b = unwrap(res);
    const totalYes = b.totalHelpfulYes ?? b.totalYes ?? 0;
    const totalNo = b.totalHelpfulNo ?? b.totalNo ?? 0;
    const totalPublished = b.totalPublished ?? b.publishedItems ?? 0;
    return {
      totalItems: b.totalItems,
      totalPublished,
      totalYes,
      totalNo,
      overallRatio: b.overallHelpfulRatio ?? b.overallRatio ?? 0,
      mostHelpful: (b.mostHelpful ?? []).map(mapAnalyticsRow),
      leastHelpful: (b.leastHelpful ?? []).map(mapAnalyticsRow),
      mostVoted: (b.mostVoted ?? []).map(mapAnalyticsRow),
      // Backward-compat shims for dashboard.
      total: b.totalItems,
      totalViews: totalYes + totalNo,
    };
  },
};
